import { Badge, Text } from '@mantine/core';
import { useModals } from '@mantine/modals';
import moment from 'moment';

import { EORIGIN_DATE_FORMAT_WITH_TIME, ProvidedDocuments } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { TableActions, TableColumnProps } from '@components';
import { useDeleteProvidedDocument } from '@hooks/declaration/useDeleteProvidedDocument';
import { useDownloadProvidedDocument } from '@hooks/declaration/useDownloadProvidedDocument';

interface UseColumnsProps {
  declarationId: string;
}

export const useControlProvidedDocumentsColumns = ({ declarationId }: UseColumnsProps): TableColumnProps<ProvidedDocuments>[] => {
  const modals = useModals();
  const downloadMutation = useDownloadProvidedDocument();
  const deleteMutation = useDeleteProvidedDocument();

  const handleDownload = (row: ProvidedDocuments) => {
    if (!row.cloudFileName) return;
    downloadMutation.mutate({
      declarationId,
      fileName: row.cloudFileName,
    });
  };

  const handleDelete = (row: ProvidedDocuments) => {
    modals.openConfirmModal({
      title: 'Confirm delete',
      children: <Text>Are you sure you want to delete this document?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate({
          declarationId,
          documentId: row._id,
        });
      },
    });
  };

  return [
    {
      name: 'Type',
      cell: (row: ProvidedDocuments) => row.type,
    },
    {
      name: 'Reference number',
      cell: (row: ProvidedDocuments) => row.referenceNumber || '',
    },
    {
      name: 'Description',
      cell: (row: ProvidedDocuments) => row.description,
      width: 300,
    },
    {
      name: 'Response date',
      cell: (row: ProvidedDocuments) => (row.responseDate ? moment(row.responseDate).format(EORIGIN_DATE_FORMAT_WITH_TIME) : ''),
    },
    {
      name: 'File name',
      cell: (row: ProvidedDocuments) => row.fileName || '',
    },
    {
      name: 'Status',
      cell: (row: ProvidedDocuments) =>
        row.status ? (
          <Badge color={row.status === 'SENDING_FAILED' ? 'red' : 'green'} variant="light" size="sm">
            {row.status === 'SENDING_FAILED' ? 'Sending failed' : 'Sent'}
          </Badge>
        ) : null,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: ProvidedDocuments) => (
        <TableActions
          actions={[
            {
              icon: IconFactory.download,
              text: 'Download',
              onClick: () => handleDownload(row),
              disabled: !row.cloudFileName,
            },
            {
              icon: IconFactory.delete,
              text: 'Delete',
              onClick: () => handleDelete(row),
              if: !row.responseDate || row.status === 'SENDING_FAILED',
            },
          ]}
        />
      ),
    },
  ];
};
